import React, { useState, useRef, useEffect } from "react";
import { Video, VideoOff, Mic, MicOff, FileText, Volume2, X, Target } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, Link } from "react-router-dom";
import Sidebar from "./component/peermatesidebar";
import Navbar from "../../components/Navbar";
import './component/Avatar.css';

const PracticeTestUI = () => {
  const location = useLocation();
  const {
    avatar = '/e007f099a7f5f305a19330ae4be1e77c82513a49.png', // Default avatar
    course = 'General Course',
    focus = 'General Learning'
  } = location.state || {};

  const [isVideoOn, setIsVideoOn] = useState(false);
  const [isMicOn, setIsMicOn] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [aiMessage, setAiMessage] = useState("");
  const [subscriptionActive, setSubscriptionActive] = useState(true); // Default to true for loader, check in useEffect
  const [isLoading, setIsLoading] = useState(true);
  const [isSessionActive, setIsSessionActive] = useState(false);

  const student = React.useMemo(() => {
    try {
      const raw = localStorage.getItem('student');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    const checkSub = async () => {
      if (!student?.id) {
          setSubscriptionActive(false);
          setIsLoading(false);
          return;
      }
      try {
        const res = await fetch(`${process.env.REACT_APP_BASE_URL}/api/students/subscriptions/me/${student.id}`);
        const data = await res.json();
        setSubscriptionActive(Boolean(data?.data?.isActive));
      } catch (err) {
        setSubscriptionActive(false);
      } finally {
        setIsLoading(false);
      }
    };
    checkSub();
  }, [student?.id]);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef(null);
  const audioRef = useRef(null); // For TTS playback

  /* ================= CLEANUP ================= */

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  /* ================= AI LOGIC ================= */

  const speak = async (text) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/ai/speak`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      });

      const contentType = response.headers.get('content-type');

      // If backend returns JSON, it means ElevenLabs failed/is blocked
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        if (data.fallback) {
          triggerBrowserTTS(text);
          return;
        }
      }

      if (!response.ok) throw new Error('Failed to fetch audio');

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      
      if (audioRef.current) {
        audioRef.current.pause();
      }
      
      const audio = new Audio(url);
      audio.onended = () => URL.revokeObjectURL(url);
      audio.onerror = () => URL.revokeObjectURL(url);
      audioRef.current = audio;
      audio.play();
    } catch (error) {
      console.error('TTS Error:', error);
      triggerBrowserTTS(text);
    }
  };

  const triggerBrowserTTS = (text) => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      window.speechSynthesis.speak(utterance);
    }
  };

  const handleAiResponse = async (userText) => {
    if (!userText || !userText.trim()) {
      console.warn("Empty user text, skipping AI call");
      return;
    }
    setIsTyping(true);
    setAiMessage("Thinking...");

    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/ai/peermate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userText,
          course: course,
          focus: focus,
          studentId: student?.id,
          history: [] // Simplified for video mode
        }),
      });

      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Server did not return JSON');
      }

      const data = await response.json();
      if (data.sender === 'bot') {
        setAiMessage(data.text);
        speak(data.text);
      }
    } catch (error) {
      console.error('Error calling AI:', error);
      setAiMessage("I'm having trouble connecting to the brain right now. Please try again.");
    } finally {
      setIsTyping(false);
    }
  };

  /* ================= CAMERA ================= */

  const startCamera = async () => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: isMicOn,
      });

      streamRef.current = stream;
      setIsVideoOn(true);
    } catch (error) {
      console.error(error);
      alert("Camera permission denied or unavailable.");
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsVideoOn(false);
  };

  const toggleCamera = () => {
    isVideoOn ? stopCamera() : startCamera();
  };

  /* ================= MICROPHONE (STT) ================= */

  const toggleMic = async () => {
    if (isRecording) {
      stopRecording();
      return;
    }
    startRecording();
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        handleTranscription(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setIsMicOn(true);
    } catch (err) {
      console.error('Error accessing microphone:', err);
      alert('Microphone access denied or unavailable.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsMicOn(false);
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
    }
  };

  const handleTranscription = async (blob) => {
    const formData = new FormData();
    formData.append('file', blob, 'recording.wav');

    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/ai/transcribe`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Transcription server error: ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Transcription server did not return JSON');
      }

      const data = await response.json();
      if (data.success && data.text) {
        handleAiResponse(data.text);
      }
    } catch (error) {
      console.error('Transcription error:', error);
      setAiMessage("I couldn't hear you clearly. Could you try again?");
    }
  };

  const handleStartSession = async () => {
    setIsSessionActive(true);
    // Automate hardware activation
    if (!isVideoOn) await startCamera();
    if (!isRecording) await startRecording();

    handleAiResponse(`Hello! I'm PeerMate, your AI proctor. I'm ready to start the practice session for ${course}. We will focus on ${focus}. Are you ready to begin?`);
  };

  const handleEndSession = () => {
    setIsSessionActive(false);
    stopCamera();
    stopRecording();
    setAiMessage("Session ended. Great job today!");
  };

  /* ================= ATTACH STREAM ================= */

  useEffect(() => {
    if (isVideoOn && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [isVideoOn]);

  /* ================= CLEANUP ================= */

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">

      {/* NAVBAR */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <Navbar />
      </div>

      <div className="flex pt-16">

        {/* SIDEBAR */}
        <div className=" md:block">
          <Sidebar />
        </div>

        {/* MAIN CONTENT */}
        <main className="flex-1 mt-20 lg:mt-2 w-full relative">
          
          {isLoading ? (
            <div className="flex items-center justify-center min-h-[60vh]">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : !subscriptionActive ? (
            <div className="flex flex-col items-center justify-center min-h-[80vh] bg-white/80 backdrop-blur-sm z-10 p-6 text-center">
              <div className="max-w-md bg-white p-8 rounded-2xl shadow-xl border border-blue-100">
                <VideoOff className="w-16 h-16 text-blue-600 mb-6 mx-auto opacity-20" />
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Premium Feature</h2>
                <p className="text-gray-600 mb-8">
                  Video interactive sessions with PeerMate AI are available for premium subscribers only. 
                  Enjoy unlimited chat and voice sessions on the free plan, or upgrade to unlock video proctoring.
                </p>
                <Link to="/subscription" className="inline-block bg-blue-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-blue-700 transition shadow-lg">
                  Upgrade to Premium
                </Link>
              </div>
            </div>
          ) : (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

            {/* HEADER */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-6">

              <div>
                <h1 className="text-lg sm:text-xl lg:text-2xl font-bold">
                  {course} - Practice Test
                </h1>

                <p className="text-gray-500 text-xs sm:text-sm">
                  Focus: {focus}. Test your understanding and see how well you understand each topic.
                </p>
              </div>

              <div className="flex items-center gap-2">
                {isSessionActive && (
                  <span className="flex items-center gap-1.5 px-3 py-1 bg-red-500/10 text-red-500 rounded-full text-[10px] font-black uppercase tracking-widest animate-pulse border border-red-500/20">
                    <div className="w-1.5 h-1.5 bg-red-500 rounded-full" />
                    Proctored
                  </span>
                )}
                <span className="text-blue-600 font-medium text-xs sm:text-sm">
                  {isSessionActive ? 'Live Session Active' : 'Waiting for Proctor'}
                </span>
              </div>

            </div>


              {/* VIDEO + SCORE ROW */}
              <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">

                {/* VIDEO */}
                <div className="relative w-full flex-1 bg-gray-900 rounded-2xl overflow-hidden shadow-2xl aspect-video md:aspect-auto md:h-[60vh]">
                  
                  {/* AI IMAGE / AVATAR */}
                  <div className={`absolute inset-0 w-full h-full transition-all duration-700 ${isTyping ? 'avatar-talk' : 'avatar-breathe'}`}>
                    <img src={avatar} className="w-full h-full object-cover" alt="AI Proctor" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20" />
                  </div>

                  {/* AI Response Overlay */}
                  <AnimatePresence>
                  {aiMessage && (
                    <motion.div 
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="absolute bottom-6 left-6 right-24 md:left-10 md:right-32 bg-white/10 backdrop-blur-xl p-4 md:p-6 rounded-2xl border border-white/20 shadow-2xl"
                    >
                      {isSessionActive && (
                        <div className="flex items-center gap-2 mb-2">
                           <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse shadow-[0_0_10px_red]" />
                           <span className="text-[10px] text-white/80 font-black uppercase tracking-widest">Live Proctoring</span>
                        </div>
                      )}
                      <p className="text-white text-sm md:text-lg font-medium leading-relaxed italic">
                        "{aiMessage}"
                      </p>
                    </motion.div>
                  )}
                  </AnimatePresence>

                  {/* USER CAMERA PIP */}
                  {isVideoOn && (
                    <video
                      ref={videoRef}
                      autoPlay
                      muted
                      playsInline
                      className="absolute top-4 right-4 w-24 h-24 md:w-40 md:h-40 rounded-2xl border-2 border-white/30 object-cover shadow-2xl transition-all hover:scale-105 z-20"
                    />
                  )}
                </div>

                {/* PERFORMANCE HUD / SCORE */}
                <div className="lg:w-72 flex flex-col gap-4">
                  <div className="bg-white/80 backdrop-blur-md p-6 rounded-2xl shadow-xl border border-blue-50">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-[10px] font-black uppercase text-gray-400 tracking-widest">Mastery Score</span>
                      <Target size={16} className="text-blue-500" />
                    </div>
                    <div className="text-3xl font-black text-blue-600">84<span className="text-sm text-gray-400 ml-1">/100</span></div>
                    <div className="mt-4 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500 rounded-full" style={{ width: '84%' }} />
                    </div>
                  </div>

                  <div className="bg-blue-600 p-6 rounded-2xl shadow-xl shadow-blue-100 flex flex-col gap-2">
                    <span className="text-[10px] font-black uppercase text-white/60 tracking-widest">Session Status</span>
                    <p className="text-white font-bold text-sm">
                      {isSessionActive ? 'Analyzing engagement...' : 'Ready to start proctoring'}
                    </p>
                  </div>
                </div>

              </div>


            {/* CONTROLS */}
            <div className="mt-8 flex justify-center">

              <div className="
                grid
                grid-cols-3
                gap-4 sm:gap-6
                bg-white
                px-6 py-4
                rounded-xl
                shadow
              ">

                <ControlButton
                  active={isVideoOn}
                  onClick={toggleCamera}
                  icon={isVideoOn ? <Video size={20} /> : <VideoOff size={20} />}
                  label="Camera"
                />

                <ControlButton
                  active={isRecording}
                  onClick={toggleMic}
                  icon={isRecording ? <MicOff size={20} /> : <Mic size={20} />}
                  label={isRecording ? "Recording..." : "Speak"}
                  className={isRecording ? "mic-active" : ""}
                />

                {isSessionActive ? (
                  <button onClick={handleEndSession} className="flex flex-col items-center gap-1 group">
                    <div className="bg-red-600 group-hover:bg-red-700 text-white p-3 rounded-lg transition shadow-lg shadow-red-100">
                      <X size={20} />
                    </div>
                    <span className="text-xs font-semibold text-red-600">
                      End Session
                    </span>
                  </button>
                ) : (
                  <button onClick={handleStartSession} className="flex flex-col items-center gap-1 group">
                    <div className="bg-green-600 group-hover:bg-green-700 text-white p-3 rounded-lg transition shadow-lg shadow-green-100">
                      <FileText size={20} />
                    </div>
                    <span className="text-xs font-semibold text-green-600">
                      Join Session
                    </span>
                  </button>
                )}

              </div>

            </div>


            </div>
          )}

        </main>

      </div>

    </div>
  );
};


/* BUTTON */

const ControlButton = ({ icon, label, active, onClick }) => (
  <button
    onClick={onClick}
    className="flex flex-col items-center gap-1"
  >

    <div
      className={`
        p-3 rounded-lg transition
        ${active ? "bg-blue-600 text-white" : "bg-gray-800 text-white"}
      `}
    >
      {icon}
    </div>

    <span className="text-xs text-gray-500">
      {label}
    </span>

  </button>
);

export default PracticeTestUI;
